import type { BackupHealth, ReadinessLevel } from '../../api';
import { Callout } from '../primitives';
import type { CalloutTone } from '../primitives';
import { actionLabel } from '../operation/actionLabels';
import { relativeTime } from './projectHealth';

const tone: Record<ReadinessLevel, CalloutTone> = { ok: 'info', warning: 'warning', critical: 'danger', unknown: 'info' };

/** The one name for taking a backup after one failed. */
export const backUpAgainLabel = 'Back up again';

/**
 * BackupPosition states where this environment's backups stand, in the
 * server's words (domain.DeriveBackupHealth, CO-95): the same sentence the
 * Overview tile, the readiness ledger and the portfolio use. A backup that
 * failed, never finished or was never restore-tested is said so here, above
 * the list, rather than left for the reader to infer from row statuses.
 *
 * When the health is not known it says that, and why, instead of showing
 * nothing: an unknown backup position must never read as a healthy one.
 * The actions are only ones the product can perform: View error opens the
 * failed backup's run, and Back up again opens the same dialog as the list.
 */
export function BackupPosition({
  health,
  environmentName,
  loading = false,
  error,
  onViewError,
  onBackUp,
}: {
  health: BackupHealth | null;
  environmentName: string;
  /** The health request is still on its way. */
  loading?: boolean;
  /** Why the health could not be read, when the request failed. */
  error?: string | null;
  /** Opens the run of the newest failed backup, when there is one. */
  onViewError?: () => void;
  onBackUp?: () => void;
}) {
  if (!health) {
    if (loading) {
      return (
        <p className="backup-line backup-line--pending" role="status" data-testid="backup-position">
          {environmentName}: checking the backup status…
        </p>
      );
    }
    return (
      <Callout tone="info" role="status" title={`${environmentName}: backup status not known`} testId="backup-position">
        <p>{error ? `The backup status could not be read: ${error}` : 'Not reported, so it is not known whether backups are current.'}</p>
      </Callout>
    );
  }
  // Healthy is a compact line, not a green banner: the facts, then nothing.
  // Every other level keeps the callout, with the server's own detail.
  if (health.level === 'ok') {
    return (
      <p className="backup-line" role="status" data-testid="backup-position">
        <span className="backup-line__fact">
          Last backup · <time dateTime={health.last_success_at}>{relativeTime(health.last_success_at)}</time>
        </span>
        <span className="backup-line__fact">{health.headline}</span>
        {health.scheduled && health.frequency ? <span className="backup-line__fact">Backups {health.frequency}</span> : null}
      </p>
    );
  }
  // Only a failed backup is retried: a backup that is still running is not.
  const failing = health.state === 'failing';
  // A standing state, not an event: announced politely, never as an alert.
  return (
    <Callout
      tone={tone[health.level]}
      role="status"
      title={`${environmentName}: ${health.headline}`}
      testId="backup-position"
      actions={
        failing && (onViewError || onBackUp) ? (
          <>
            {onViewError ? (
              <button type="button" className="button button--secondary button--small" onClick={onViewError} data-testid="backup-view-error">
                {actionLabel.viewError}
              </button>
            ) : null}
            {onBackUp ? (
              <button type="button" className="button button--secondary button--small" onClick={onBackUp} data-testid="backup-back-up-again">
                {backUpAgainLabel}
              </button>
            ) : null}
          </>
        ) : undefined
      }
    >
      <p>
        {health.detail}
        {health.scheduled && health.frequency ? <span className="muted"> Backups run {health.frequency}.</span> : null}
      </p>
      {health.failure_cause && health.failure_cause !== 'unknown' ? (
        <p data-testid="backup-cause">
          <strong>Reported cause:</strong> {health.failure_cause_title}.{health.failure_fix ? ` ${health.failure_fix}` : ''}
        </p>
      ) : null}
    </Callout>
  );
}
